import { z } from "zod";
import dayjs, { Dayjs } from "dayjs";
import { refinements } from "@helpers/refinements";
import i18n from "@/i18n";
import { BadgeStatus, Picking, SaleOrder } from "@/types/general";

const availableServiceScheme = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string()
});

const packageCustom = z.object({
    id: z.string(),
    width: z.number(),
    length: z.number(),
    height: z.number()
});

const packageReference = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
    width: z.number(),
    length: z.number(),
    height: z.number()
});

export type PackageCustomTyping = z.infer<typeof packageCustom>;
export type PackageReferenceTyping = z.infer<typeof packageReference>;

export const packageConfig = z.union([
    packageCustom,
    packageReference
]);

export type PackageConfig = z.infer<typeof packageConfig>;

export const createPickingFormScheme = (allowNegativeStock: boolean) => z.object({
    extractedFromTransit: z.boolean(),

    customer: z.object({
        id: z.number(),
        code: z.string().nullish(),
        name: z.string()
    }),

    sale_order: z.object({
        id: z.number(),
        code: z.string().nullish(),
        is_invoiced: z.boolean(),
        status: z.object({
            name: z.nativeEnum(BadgeStatus.Base),
            meta: z.object({
                cancel_reason: z.string().nullish()
            }).nullish()
        })
    }).readonly(),

    order_destination_country: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string()
    }).nullish(),

    picking_date: z.object({
        date: z.instanceof(dayjs as unknown as typeof Dayjs).refine(refinements.validDate.refine, refinements.validDate.message)
    }),

    lines: z
        .array(
            z
                .object({
                    type: z.string(),
                    module: z.custom<SaleOrder.Components.Group["data"]["module"]>(),
                    name: z.string().nullish(),
                    data: z.object({
                        id: z.number().nullish(),
                        comment: z.string().nullish(),
                        product: z.custom<SaleOrder.Components.LineData["product"]>(),
                        alternative_name: z.string().nullish(),
                        already_picked_quantity: z.coerce.number(),
                        // Used for services, since they don't require locations.
                        // Preprocessing converts zeros and negative values to undefined,
                        // treating them as "not picked" to avoid validation errors.
                        picked_quantity: z
                            .preprocess(
                                (value) => {
                                    const coercedValue = Number(value);

                                    if (!Number.isFinite(coercedValue) || coercedValue <= 0) {
                                        return undefined;
                                    }

                                    return coercedValue;
                                },
                                z.number().positive().optional()
                            ),
                        quantity: z.coerce.number(),
                        // What is left of the line once its reserved places are served (`0` when the whole
                        // remainder is reserved somewhere). Services and unreserved lines carry the full remainder.
                        free_to_pick_anywhere: z.coerce.number(),
                        picks: z.array(
                            z
                                .object({
                                    // Set on rows seeded from the line's reserved places: that quantity may
                                    // only be picked here, so the row keeps its location.
                                    reserved_place: z
                                        .object({
                                            quantity: z.number()
                                        })
                                        .optional(),
                                    outgoing_location: z
                                        .object({
                                            location_id: z.number(),
                                            sub_location_id: z.number().nullable().optional(),
                                            location_name: z.string(),
                                            section_name: z.string().nullable().optional(),
                                            in_stock: z.number(),
                                            // Absent while picking isn't gated on it
                                            available_here: z.number().optional()
                                        })
                                        .optional(),
                                    // Preprocessing converts zeros and negative values to undefined,
                                    // allowing users to select a location without entering quantity
                                    // (or enter 0) without triggering validation errors.
                                    picked_quantity: z
                                        .preprocess(
                                            (value) => {
                                                const coercedValue = Number(value);

                                                if (!Number.isFinite(coercedValue) || coercedValue <= 0) {
                                                    return undefined;
                                                }

                                                return coercedValue;
                                            },
                                            z.number().positive().optional()
                                        )
                                })
                                .refine(
                                    (scheme) => {
                                        // With negative stock allowed, a pick may exceed what the location holds.
                                        if (allowNegativeStock) return true;

                                        // `available_here` already accounts for the older reservations of other
                                        // orders, so it is the real ceiling whenever the backend sends it. It is
                                        // absent for companies allowing negative stock, which return above anyway.
                                        const availableQuantity =
                                            scheme.outgoing_location?.available_here ?? scheme.outgoing_location?.in_stock ?? 0;

                                        return (scheme.picked_quantity ?? 0) <= availableQuantity;
                                    },
                                    {
                                        message: i18n.t("sales.picking.mutation.validation.pickedQuantityCannotBeGreaterThanInStock"),
                                        path: ["picked_quantity"]
                                    }
                                )
                                .superRefine((scheme, ctx) => {
                                    const hasPickedQuantity = typeof scheme.picked_quantity === "number" && scheme.picked_quantity > 0;

                                    // Only require location if there's an actual picked quantity > 0.
                                    // This allows zeros/undefined quantities without blocking submission.
                                    if (hasPickedQuantity && !scheme.outgoing_location) {
                                        ctx.addIssue({
                                            code: z.ZodIssueCode.custom,
                                            message: i18n.t("sales.picking.mutation.validation.outgoingLocationIsRequired"),
                                            path: ["outgoing_location"]
                                        });
                                    }
                                })
                        ),
                        serial_numbers: z.array(
                            z.object({
                                serial_number: z.string()
                            })
                        ),
                        batch_numbers: z.array(
                            z.object({
                                batch_number: z.string(),
                                expiration_date: z
                                    .instanceof(dayjs as unknown as typeof Dayjs)
                                    .refine(refinements.validDate.refine, refinements.validDate.message)
                                    .nullish()
                            })
                        )
                    }).nullish()
                })
                .superRefine((scheme, ctx) => {
                    if (scheme.data?.product.is_service && (scheme.data?.picked_quantity ?? 0) > scheme.data.quantity) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message:  i18n.t("sales.picking.mutation.validation.pickedQuantityCannotBeGreaterThanAvailableQuantity"),
                            path: ["picked_quantity"]
                        });
                    }
                })
        )
        .refine(
            (scheme) => {
                return scheme.some((line) => {
                    return (
                        line.data?.picked_quantity ||
                        line.data?.picks.some((pick) => pick.picked_quantity && pick.outgoing_location)
                    );
                });
            },
            {
                message: i18n.t("sales.picking.mutation.validation.pickAtLeastOneProduct")
            }
        )
        .superRefine((lines, ctx) => {
            lines.forEach((line, index) => {
                if (!line.data) return;

                const totalPicked = line.data?.picks.reduce((total, pick) => total + (pick.picked_quantity || 0), 0);
                if (totalPicked > line.data?.quantity - line.data?.already_picked_quantity) {
                    line.data?.picks.forEach((pick, pickIndex) => {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: i18n.t("sales.picking.mutation.validation.totalPickedCannotExceed"),
                            path: [index, "picks", pickIndex, "picked_quantity"]
                        });
                    });
                }
            });
        })
        .superRefine((lines, ctx) => {
            lines.forEach((line, index) => {
                const data = line.data;
                if (!data) return;

                // A row seeded from a reserved place may take any quantity there. Everything picked
                // elsewhere shares what the line leaves free, which is what the backend enforces on submit.
                const pickedElsewhere = data.picks.reduce(
                    (total, pick) => (pick.reserved_place ? total : total + (pick.picked_quantity || 0)),
                    0
                );

                if (pickedElsewhere <= data.free_to_pick_anywhere) return;

                data.picks.forEach((pick, pickIndex) => {
                    if (pick.reserved_place) return;

                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: i18n.t("sales.picking.mutation.validation.pickedElsewhereExceedsFree", {
                            count: data.free_to_pick_anywhere
                        }),
                        path: [index, "picks", pickIndex, "picked_quantity"]
                    });
                });
            });
        })
        .superRefine((scheme, ctx) => {
            // Filter lines that either have serial numbers or batch numbers
            const filteredLines = scheme.filter(({ data }) => {
                if (!data) return false;

                const { is_serial_numbers, is_batch_numbers } = data?.product ?? {};
                return (
                    (is_serial_numbers || is_batch_numbers) &&
                    data?.picks.some(({ outgoing_location, picked_quantity }) => outgoing_location && picked_quantity)
                );
            });

            // Check if there are any lines with serial number errors
            const hasSerialError = filteredLines.some(({ data }) => {
                if (!data?.product.is_serial_numbers) return false;

                const totalPickedQuantity = data.picks.reduce((acc, { picked_quantity }) => acc + (picked_quantity ?? 0), 0);
                return data.serial_numbers.length !== Math.ceil(totalPickedQuantity);
            });

            // Check if there are any lines with batch number errors
            const hasBatchError = filteredLines.some(({ data }) => {
                if (!data?.product.is_batch_numbers) return false;

                const totalPickedQuantity = data.picks.reduce((acc, { picked_quantity }) => acc + (picked_quantity ?? 0), 0);
                return data.batch_numbers.length === 0 || data.batch_numbers.length > Math.ceil(totalPickedQuantity);
            });

            // Add an issue if serial number errors are found
            if (hasSerialError) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: i18n.t("sales.picking.mutation.validation.serialsFilledIncorrectly"),
                    path: ["serial_numbers"]
                });
            }

            // Add an issue if batch number errors are found
            if (hasBatchError) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: i18n.t("sales.picking.mutation.validation.batchNumbersFilledIncorrectly"),
                    path: ["batch_numbers"]
                });
            }
        }),

    packages: z.array(
        z.object({
            package: packageConfig,
            weight: z.coerce.number().nullish()
        })
    ).optional(),

    shipmondo: z.object({
        enabled: z.boolean(),
        country: z.object({
            id: z.number(),
            name: z.string(),
            code: z.string()
        }).optional(),
        carrier: z.object({
            id: z.number(),
            name: z.string(),
            code: z.string()
        }).optional(),
        product: z.object({
            id: z.number(),
            name: z.string(),
            code: z.string(),
            available_services: z.array(availableServiceScheme),
            required_services: z.array(availableServiceScheme),
            own_agreement_available: z.boolean(),
            available_customer_numbers: z.custom<{ customer_number: string }[]>()
        }).optional(),
        services: z.array(availableServiceScheme).nullish(),
        customer_number: z.custom<{ customer_number: string }>().nullish(),
        own_agreement: z.boolean().nullish()
    }).superRefine((scheme, ctx) => {
        if (scheme.enabled && !scheme.product) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t("general.validation.global.required"),
                path: ["product"]
            });
        }
        if (scheme.enabled && scheme.product?.required_services?.length && (!scheme.services || scheme.services.length === 0)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t("general.validation.global.required"),
                path: ["service"]
            });
        }

        if(scheme.product?.own_agreement_available && scheme.own_agreement && !scheme.customer_number) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t("general.validation.global.required"),
                path: ["customer_number"]
            });
        }
    })
}).superRefine((scheme, ctx) => {
    if (scheme.shipmondo?.enabled && (!scheme.packages || scheme.packages.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t("sales.picking.general.fields.packaging.validation.shipmondoRequirement"),
            path: ["packages"]
        });
    }

    if (scheme.shipmondo?.enabled) {
        if (!scheme.order_destination_country) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t("general.validation.global.required"),
                path: ["order_destination_country"]
            });
        }

        const indexesOfPackagesWithoutWeight = scheme.packages?.reduce(
            (acc, curr, index) => {
                if (!curr.weight) {
                    acc.push(index);
                }

                return acc;
            },
            [] as number[]
        );

        if (indexesOfPackagesWithoutWeight?.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t("sales.picking.general.fields.packaging.noWeightWarning"),
                path: ["packages"]
            });
        }
    }
});

export const isPackageTemporary = (packageObj: z.infer<typeof packageConfig>): packageObj is PackageCustomTyping => {
    return !("name" in packageObj) &&
        ("width" in packageObj && "length" in packageObj && "height" in packageObj);
};

export const getPackageName = (packageObj: z.infer<typeof packageConfig>): string => {
    if (isPackageTemporary(packageObj)) {
        return packageObj.width + "x" + packageObj.height + "x" + packageObj.length;
    } else {
        return packageObj.name;
    }
};

export type PickingMutationFormTyping = z.infer<ReturnType<typeof createPickingFormScheme>>;
